import { apiFetch } from "@/auth/apiClient";

// ─── Types ────────────────────────────────────────────────────────────────────

export type DiscountType = "PERCENTAGE" | "FIXED";

export type CouponTargetType =
  | "COURSE"
  | "MOCK_TEST"
  | "ALL_COURSES"
  | "ALL_MOCK_TESTS"
  | "GLOBAL";

export type ProductType = "COURSE" | "MOCK_TEST";

export type CouponStatus = "active" | "expired" | "scheduled" | "disabled";

export type Coupon = {
  id: string;
  code: string | null;
  name: string;
  description: string | null;
  discountType: DiscountType;
  discountValue: string;
  targetType: CouponTargetType;
  targetId: string | null;
  startDate: string;
  endDate: string;
  usageLimit: number | null;
  perUserLimit: number;
  totalUsed: number;
  isActive: boolean;
  isAutoPromotion: boolean;
  minimumPurchaseAmount: string | null;
  isFirstPurchaseOnly: boolean;
  createdBy: number;
  createdByUser?: { id: number; email: string };
  updatedBy?: number | null;
  createdAt: string;
  updatedAt: string;
};

export type CouponUsage = {
  id: string;
  couponId: string;
  userId: number;
  productType: ProductType;
  productId: string;
  productName: string | null;
  originalPrice: string;
  discountAmount: string;
  finalPrice: string;
  orderId: string | null;
  usedAt: string;
  user?: {
    id: number;
    email: string;
    profile?: { firstName?: string | null; lastName?: string | null } | null;
  };
  coupon?: Coupon;
};

export type CouponDiscountResult = {
  couponId: string;
  couponCode: string | null;
  couponName: string;
  discountType: DiscountType;
  discountValue: number;
  originalPrice: number;
  discountAmount: number;
  finalPrice: number;
  savings: number;
};

export type CouponAnalytics = {
  totalCoupons?: number;
  activeCoupons?: number;
  expiringThisWeek?: number;
  totalUses: number;
  totalSavings: number;
  totalRevenue: number;
  couponDetails?: Coupon;
};

export type CouponsListResponse = {
  items: Coupon[];
  total: number;
  page: number;
  limit: number;
};

export type CouponUsageListResponse = {
  items: CouponUsage[];
  total: number;
};

// ─── Create / Update / Delete ─────────────────────────────────────────────────

export type CreateCouponInput = {
  code?: string;
  name: string;
  description?: string;
  discountType: DiscountType;
  discountValue: number;
  targetType: CouponTargetType;
  targetId?: string;
  startDate: string;
  endDate: string;
  usageLimit?: number;
  perUserLimit?: number;
  isActive?: boolean;
  isAutoPromotion?: boolean;
  minimumPurchaseAmount?: number;
  isFirstPurchaseOnly?: boolean;
};

export async function createCoupon(dto: CreateCouponInput): Promise<Coupon> {
  return apiFetch<Coupon>("/lms/coupons", { method: "POST", json: dto });
}

export async function updateCoupon(
  id: string,
  dto: Partial<CreateCouponInput>,
): Promise<Coupon> {
  return apiFetch<Coupon>(`/lms/coupons/${id}`, { method: "PATCH", json: dto });
}

export async function deleteCoupon(id: string): Promise<void> {
  await apiFetch(`/lms/coupons/${id}`, { method: "DELETE" });
}

// ─── List / Get ───────────────────────────────────────────────────────────────

export async function getCoupons(params: {
  status?: CouponStatus | "all";
  search?: string;
  targetType?: CouponTargetType;
  page?: number;
  limit?: number;
} = {}): Promise<CouponsListResponse> {
  const qp: Record<string, string> = {
    page: String(params.page ?? 1),
    limit: String(params.limit ?? 20),
    status: params.status ?? "all",
  };
  if (params.search) qp.search = params.search;
  if (params.targetType) qp.targetType = params.targetType;
  const qs = new URLSearchParams(qp);
  return apiFetch<CouponsListResponse>(`/lms/coupons?${qs}`, { method: "GET" });
}

export async function getCoupon(id: string): Promise<Coupon> {
  return apiFetch<Coupon>(`/lms/coupons/${id}`, { method: "GET" });
}

export async function getCouponUsageHistory(
  couponId: string,
  params: { page?: number; limit?: number; productType?: ProductType } = {},
): Promise<CouponUsageListResponse> {
  const qp: Record<string, string> = {
    page: String(params.page ?? 1),
    limit: String(params.limit ?? 20),
  };
  if (params.productType) qp.productType = params.productType;
  const qs = new URLSearchParams(qp);
  return apiFetch<CouponUsageListResponse>(`/lms/coupons/${couponId}/usage?${qs}`, {
    method: "GET",
  });
}

// ─── Analytics ────────────────────────────────────────────────────────────────

export async function getCouponAnalytics(couponId?: string): Promise<CouponAnalytics> {
  const qs = couponId ? `?couponId=${encodeURIComponent(couponId)}` : "";
  return apiFetch<CouponAnalytics>(`/lms/coupons/analytics${qs}`, { method: "GET" });
}

// ─── Student: Validate ────────────────────────────────────────────────────────

export async function validateCoupon(dto: {
  code: string;
  productType: ProductType;
  productId: string;
  originalPrice: number;
}): Promise<CouponDiscountResult> {
  return apiFetch<CouponDiscountResult>("/lms/coupons/validate", {
    method: "POST",
    json: dto,
  });
}

export async function getAutoPromotions(
  productType: ProductType,
  productId: string,
  originalPrice: number,
): Promise<CouponDiscountResult[]> {
  const qs = new URLSearchParams({
    productType,
    productId,
    originalPrice: String(originalPrice),
  });
  return apiFetch<CouponDiscountResult[]>(`/lms/coupons/auto-promotions?${qs}`, {
    method: "GET",
  });
}
